import React, { useState, useCallback, useMemo } from 'react'
import classNames from 'classnames'
import Button from '../../../atoms/button/Button'
import InlineGroup from '../../../atoms/inlinegroup/InlineGroup'
import VerticalGroup from '../../../atoms/verticalgroup/VerticalGroup'
import SimulationSetting from './SimutationSetting'
import Text from '../../../atoms/text/Text'
import SimpleModal from '../../../organisms/standard-modal/SimpleModal'
import useBrandings, { useBranding } from '../../../../hooks/useBrandings'
import LoadingSpinner from '../../../atoms/loadingspinner/LoadingSpinner'
import BrandingPreview, { AsyncBrandingPreview } from '../../../organisms/brandingpreview/BrandingPreview'
import './SelectBranding.scss'
import Clickable from '../../../atoms/clickable/Clickable'
import ErrorModal from '../../../organisms/standard-modal/ErrorModal'

interface PreviewModalProps {
  isOpen: boolean
  onClose: () => void
  brandingId: string
  'data-test'?: string
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  brandingId,
  ...props
}) =>
  <SimpleModal
    size='medium'
    data-test={props['data-test']}
    isMinimal
    showCloseButton
    isOpen={isOpen}
    onClose={onClose}
  >
    <AsyncBrandingPreview brandingId={brandingId} maxContainerHeight={425} />
  </SimpleModal>


interface BrandingSelectOptionProps {
  branding: API.BrandingResponse
  onBrandingSelected: (branding: API.BrandingResponse) => void
  'data-test'?: string
}
const BrandingSelectOption: React.FC<BrandingSelectOptionProps> = ({
  branding,
  onBrandingSelected,
  ...props
}) => {
  const onClick = useCallback(() => {
    onBrandingSelected(branding)
  }, [onBrandingSelected, branding])

  return <VerticalGroup 
    className={classNames('branding-select-option')}
  >
    <BrandingPreview
      branding={branding}
      maxContainerHeight={195}
    />
    <VerticalGroup center verticalCenter wide flexAuto>
      <Button
        data-test={props['data-test']}
        onClick={onClick}>Select</Button>
    </VerticalGroup>
  </VerticalGroup>
}

interface SelectModalProps {
  isOpen: boolean
  onClose: () => void
  clientId: string
  onBrandingSelected: (branding: API.BrandingResponse) => void
  'data-test'?: string
}

const SelectModal: React.FC<SelectModalProps> = ({
  clientId,
  isOpen,
  onClose,
  onBrandingSelected,
  ...props
}) => {
  const { data, inProgress } = useBrandings()
  const onBranding = useCallback((branding: API.BrandingResponse) => {
    onBrandingSelected(branding)
    onClose()
  }, [onBrandingSelected, onClose])

  const selectedData = useMemo(() => {
    if (!data) return []
    return data?.filter(branding => {
      return branding.clientIds.includes(clientId)
    })
  }, [data, clientId])

  // const size = selectedData.length === 1
  //   ? 'small'
  //   : (selectedData.length === 2
  //     ? 'medium'
  //     : (selectedData.length >= 3
  //       ? 'large'
  //       : 'small'))

  const size = 'large'
  
  if (!inProgress && selectedData.length === 0) {
    return <ErrorModal
      isOpen={isOpen}
      onClose={onClose}
      title="No Branding Found"
      description="The selected client has not created any branding themes yet."
      {...props}
    />
  }

  return <SimpleModal
    size={size}
    isOpen={isOpen}
    showCloseButton
    onClose={onClose}
    {...props}
  >
    {inProgress && <LoadingSpinner />}
    {selectedData && <div className={classNames('branding-select-container', `size-${size}`)}>
      {selectedData.map(branding => <BrandingSelectOption
        key={branding.id}
        data-test={`branding-option-${branding.id}`}
        branding={branding}
        onBrandingSelected={onBranding}
      />)}
    </div>
    }
  </SimpleModal>
}


interface SelectBrandingProps {
  brandingId?: string
  branding?: API.BrandingResponse
  clientId?: string
  readOnly?: boolean
  onUnselectBranding?: () => void
  onBrandingSelected?: (branding: API.BrandingResponse) => void
}

const BrandingGameName = ({ id }: { id: string }) => {
  const { data } = useBranding(id)
  return data && <Text size="sm">{data.gameName}</Text>
}

const SelectBranding: React.FC<SelectBrandingProps> = ({
  brandingId,
  branding,
  clientId,
  readOnly,
  onBrandingSelected,
  onUnselectBranding
}) => {
  const isSelected = !!brandingId
  const hasClientId = !!clientId

  const [shouldShowPreviewModal, setShouldShowPreviewModal] = useState(false)
  const [shouldShowSelectionModal, setShouldShowSelectionModal] = useState(false)

  const onSelectBrandingButtonClicked = useCallback(() => {
    setShouldShowSelectionModal(true)
  }, [setShouldShowSelectionModal])

  const onCloseSelectBrandingModal = useCallback(() => {
    setShouldShowSelectionModal(false)
  }, [setShouldShowSelectionModal])

  const onPreviewClicked = useCallback(() => {
    setShouldShowPreviewModal(true)
  }, [setShouldShowPreviewModal])

  const onClosePreviewModal = useCallback(() => {
    setShouldShowPreviewModal(false)
  }, [setShouldShowPreviewModal])

  const onTrashClick = useCallback(() => {
    if (onUnselectBranding) {
      onUnselectBranding()
    }
  }, [onUnselectBranding])

  const onBrandingSelectedWrapped = useCallback((branding: API.BrandingResponse) => {
    if (onBrandingSelected) {
      onBrandingSelected(branding)
    }
  }, [onBrandingSelected])

  return <>
    <SimulationSetting icon="branding" readOnly={readOnly} onTrashClick={onTrashClick} warning={readOnly && !branding}>
      <InlineGroup spaceLeft={6} stretch spread>
        <VerticalGroup spread={!readOnly}>
          <Text size="sm" bold>Branding</Text>
          {readOnly && branding && <Text size="sm">{branding.gameName} Selected</Text>}
          {readOnly && !branding && <Text size="sm" colour='danger' bold>No branding selected</Text>}
          {!readOnly && !hasClientId && <Text size="sm">*Please select client first*</Text>}
          {!readOnly && isSelected && hasClientId && brandingId && <BrandingGameName id={brandingId} />}
          {!readOnly && <Button data-test="select-branding-button" link onClick={onSelectBrandingButtonClicked} disabled={!hasClientId}>
            {isSelected ? 'Change Branding' : 'Select Branding'}
          </Button>}
        </VerticalGroup>
        {
          isSelected && brandingId
            ? <Clickable data-test="select-branding-preview-button" onClick={onPreviewClicked} style={{ width: 85 }}>
              <AsyncBrandingPreview brandingId={brandingId} maxContainerHeight={64}/>
            </Clickable>
            : null
        }
      </InlineGroup>
    </SimulationSetting>
    {brandingId && <PreviewModal
      data-test="select-branding-preview-modal"
      isOpen={shouldShowPreviewModal}
      onClose={onClosePreviewModal}
      brandingId={brandingId}
    />}
    {clientId && <SelectModal
      data-test='select-branding-modal'
      isOpen={shouldShowSelectionModal}
      onClose={onCloseSelectBrandingModal}
      clientId={clientId}
      onBrandingSelected={onBrandingSelectedWrapped}
    />}
  </>
}

export default SelectBranding
